import { translate, repositoryActions } from '../services';

/* @class ServiceExtensions
 * Offers a way to extend the dataservice with new dispatch actions based on certain action types
 * This keeps the dataservice clean, and each store can implement his own actions, with appropriate callbacks
 */
class ServiceExtension {
  constructor() {
    this.extensions = { };
  }
  /* @method add
   * @param {string} actionType On which action should it respond, should be unique throughout the extensions
   * @param {function} executeFunc Which function should be called
   * @param {any} either null, {type:'action_type'} or [{type: 'action_type'}], defines the action that should run after the action went through
   * @param {function} transformActionFn A function that will receive the action, and returns an array of arguments
   * @param {object} context The context that applies for this function, most commonly null
   * @param {boolean} canFail if the operation cannot fail, leave this as default false, if it should propagate the failure, set it to true
   */
  add( actionType, executeFunc, dispatchAfter, transformActionFn, context, canFail = false ) {
    if ( !executeFunc || !executeFunc.apply ) {
      throw 'ArgumentException: argument executeFunc is a required function argument';
    }
    if ( transformActionFn && !transformActionFn.apply ) {
      throw 'ArgumentException: expected transformActionFn to be a function';
    }
    this.extensions[actionType] = {
      fn: executeFunc,
      context,
      transform: transformActionFn,
      dispatchAfter,
      canFail
    };
  }
  /* @method remove
   * @param {string} actionType removes a potentially previously registered action
   */
  remove( actionType ) {
    delete this.extensions[actionType];
  }
}

/*
 * @method dispatchAfter
 * @param {type} store The store that handles the dispatch request
 * @param {type} dispatches An object, either null or { type: } or [{type:}, {type:}]
 */
function dispatchAfter( store, dispatches ) {
  if ( !dispatches ) {
    return [];
  }
  const promises = [];
  if ( Array.isArray( dispatches ) ) {
    for ( let i = 0; i < dispatches.length; i++ ) {
      promises.push( store.dispatch( dispatches[i] ) );
    }
  } else {
    promises.push( store.dispatch( dispatches ) );
  }
  return promises;
}

/*
 * @const a shared ServiceExtension for the entire project
 */
const extensionService = new ServiceExtension();

/*
 * 
 * @param {type} next
 * Handles the next statement in a dispatch series
 * @returns {Function}
 */
const handleNext = next => arg => !arg || !arg.type ? arg : next( arg );

/*
 * @method dataService, the default middleware for the react-redux dispatch service
 * This function is only used as an intermediary method for the extensionService
 * It will forward all matching actions to the registered functions
 * @returns a promise so that it can be hooked upon by dispatchers using then or catch
 */
const dataService = store => next => action => {
  let $q, $qArgs;
  next( { type: 'dispatch-busy', action: action.type } );
  // call matching execution functions
  Object.keys( extensionService.extensions ).forEach( key => {
    if ( key === action.type ) {
      let ext = extensionService.extensions[key], result, args = [action];
      if ( ext.transform ) {
        args = ext.transform( action );
        if ( !Array.isArray( args ) ) {
          console.warn( `Result of transformation function is not an Array for ${key}, it will be mapped like one` );
          args = [args];
        }
      }
      result = ext.fn.apply( ext.context, args );
      $qArgs = ext.dispatchAfter;
      if ( result && result.then ) {
        // promise based service
        $q = result.then( handleNext( next ) ); /* catch should be handled by callers */ //.catch( err => console.error( err ) );
        if ( !ext.canFail ) {
          $q = $q.catch( err => {
            console.error( err );
          } );
        }
        return;
      }
      // call the next step on the next iteration
      $q = new Promise( function ( resolve, reject ) {
        setTimeout( () => {
          try {
            resolve( handleNext( next )( result ) );
          } catch ( e ) {
            reject( e );
          }
        }, 0 );
      } );
    }
  } );
      
  // dispatch after are potentially queued events
  // this really means that we should await all queued events before returning from the dataservice
  const promiseChain = [$q || new Promise( resolve => resolve( handleNext( next )( action ) ) )].concat( dispatchAfter( store, $qArgs ) );
      
  return Promise.all( promiseChain ).then( args => {
    next( { type: 'dispatch-complete', action: action.type } );
    // return the first of the promise chain arguments
    return args[0];
  } );
};

export { dataService, extensionService };